// Central, reusable API error handling.
//
// One typed error (`ApiRequestError`) is thrown by every API call, a friendly
// message mapper turns it into something a non-technical user can read and
// screenshot, and a small global registry lets any failure surface as a toast
// with a support reference code — so a raw `{"error":{"code":"UNAUTHORIZED"}}`
// JSON blob never reaches a user again.

export interface ApiErrorInit {
  message: string;
  code?: string;
  status?: number;
  requestId?: string;
}

/**
 * The single error type thrown by the API client. Carries the backend error
 * code, HTTP status, and correlation id (X-Request-ID) so callers and the global
 * handler can show actionable, support-friendly messages.
 */
export class ApiRequestError extends Error {
  readonly code?: string;
  readonly status?: number;
  readonly requestId?: string;

  constructor({ message, code, status, requestId }: ApiErrorInit) {
    super(message);
    this.name = 'ApiRequestError';
    this.code = code;
    this.status = status;
    this.requestId = requestId;
  }
}

function baseFriendlyMessage(error: ApiRequestError): string {
  switch (error.code) {
    case 'UNAUTHORIZED':
    case 'SESSION_EXPIRED':
      return 'Your session has expired. Please sign in again.';
    case 'FORBIDDEN':
      return "You don't have permission to do that. If you think you should, contact your administrator.";
    case 'FILE_NOT_FOUND':
    case 'FILE_PATH_MISSING':
    case 'FILE_MISSING':
      return "We couldn't open that file. It may have been moved or deleted.";
    case 'NOT_FOUND':
    case 'APPLICATION_NOT_FOUND':
      return "We couldn't find what you were looking for. It may have been moved or deleted.";
    case 'VALIDATION_ERROR':
      return 'Some of the information was invalid. Please check the form and try again.';
  }

  const status = error.status ?? 0;
  if (status === 403) return "You don't have permission to do that.";
  if (status === 404) return "We couldn't find what you were looking for.";
  if (status === 0) return "We couldn't reach the server. Check your connection and try again.";
  if (status >= 500) return 'Something went wrong on our end. Please try again shortly.';

  // Fall back to the backend's own message when it is presentable.
  return error.message || 'Something went wrong.';
}

/**
 * Build a friendly, screenshot-able message for any thrown error, appending the
 * correlation reference when present so the user can send it to support.
 */
export function getFriendlyErrorMessage(error: unknown): string {
  if (error instanceof ApiRequestError) {
    const base = baseFriendlyMessage(error);
    return error.requestId ? `${base} (ref: ${error.requestId})` : base;
  }
  // Browser network failures (fetch rejects with a TypeError).
  if (error instanceof TypeError) {
    return "We couldn't reach the server. Check your connection and try again.";
  }
  if (error instanceof Error && error.message) return error.message;
  return 'Something went wrong.';
}

// --- Global error surface registry ------------------------------------------
// lib/* modules are non-React, so the toast function is injected at runtime by a
// small bridge component (mirrors ApiClient.setOnUnauthorized).

type ApiErrorHandler = (error: unknown) => void;
let handler: ApiErrorHandler | null = null;

export function setApiErrorHandler(fn: ApiErrorHandler | null): void {
  handler = fn;
}

/**
 * Surface an error to the user via the global handler (a toast). Safe to call
 * from anywhere. 401 / session-expiry is swallowed here because the API client
 * already redirects to login — a toast on top would be redundant and confusing.
 */
export function notifyApiError(error: unknown): void {
  // Always leave a console trace so a suppressed (401) or pre-bridge-mount error
  // is never completely invisible to a developer.
  console.error('[api] error:', error);
  if (
    error instanceof ApiRequestError &&
    (error.status === 401 || error.code === 'SESSION_EXPIRED')
  ) {
    // 401/session expiry is handled by the login redirect — no toast.
    return;
  }
  handler?.(error);
}
